import { MemoryBankNotes } from '@/components/memory-bank/MemoryBankNotes';
import { AIWorkflow, SystemEntity } from '@erp/shared-types';
import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { useLocalStorageValue } from '@react-hookz/web';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SmartEmailViewer } from '@/components/email-viewer/SmartEmailViewer';
import { DocumentViewer } from '@/components/common/DocumentViewer';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { PencilSimple } from '@phosphor-icons/react';
import { RemittanceLineItemsTable } from './remittance-line-items-table';
import { RemittanceVerificationPanel } from './remittance-verification-panel';
import { LinkEmailButton } from './link-email-button';
import { RemittanceFormDialog } from './RemittanceFormDialog';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  useOverrideRemittanceCustomer,
  useDeleteRemittanceLineItem,
} from '@/api/rpc/hooks/accounts-receivable/useArRemittances';
import { useToast } from 'src/context/ToastContext';
import { Currency } from '@/components/common/Currency';
import { CustomerLink } from '@/components/accounts-receivable/customer-link';
import { InvoicedCustomers } from '@/components/customer/InvoicedCustomers';
import type { RemittanceDetail } from '@erp/rpc';
import { T, useGT } from 'gt-react';

interface RemittanceDetailsProps {
  remittance: RemittanceDetail;
}

/**
 * The small grey caption above each value on the remittance card, with an
 * optional trailing control (e.g. the payer's notes) that must not add height,
 * or side-by-side fields stop lining up.
 */
function RemittanceFieldLabel({
  trailing,
  children,
}: {
  trailing?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-4 items-center gap-1.5 text-xs font-medium text-muted-foreground">
      {children}
      {trailing && (
        <span className="-my-1 inline-flex items-center">{trailing}</span>
      )}
    </div>
  );
}

/**
 * Who sent the money. Reads exactly like the invoiced customers beside it; the
 * pencil next to the name swaps in a customer search, and picking one re-attributes the payment. With no payer,
 * "Assign Customer" stays visible because the field needs a value. The
 * payer's notes sit in the label because they belong to the payer.
 */
function PayerField({ remittance }: RemittanceDetailsProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const override = useOverrideRemittanceCustomer();
  const payer = remittance.matchedCustomer;

  const handleSelect = (customerId: string | undefined) => {
    if (!customerId || customerId === payer?.id) {
      setIsEditing(false);
      return;
    }
    override.mutate(
      { remittanceAdviceId: remittance.id, customerId },
      {
        onSuccess: () => setIsEditing(false),
        onError: (err) =>
          showToast({
            title: gt('Payer change failed', {
              context: 'toast title, remittance payer override failed',
            }),
            message: err instanceof Error ? err.message : '',
            color: 'error',
            icon: 'warning',
          }),
      },
    );
  };

  return (
    <div className="min-w-0 space-y-1">
      <RemittanceFieldLabel
        trailing={
          payer && (
            <MemoryBankNotes
              showTechnicalContext={false}
              entityId={payer.id}
              entityType={SystemEntity.AR_CUSTOMER}
              staticMetadata={{
                workflow: AIWorkflow.AR_REMITTANCE_INGESTION,
                workflowStep: 'RESOLVE_INVOICE_REFERENCES',
              }}
              dynamicMetadata={{ customerId: payer.id }}
              size="sm"
              tooltipText={gt('Payer notes', {
                context:
                  'tooltip on the notes button for the payer of an AR remittance',
              })}
              // Only remittance invoice matching reads these notes (they are
              // tagged RESOLVE_INVOICE_REFERENCES), so say exactly that.
              description={
                <T context="describes customer notes, used only when matching a remittance's invoice references to invoices">
                  Tell us how to match this customer&apos;s remittance invoice
                  references to our invoices. For example: &quot;For INV-1234,
                  remove INV- and match 1234.&quot;
                </T>
              }
            />
          )
        }
      >
        <T context="label, the customer who sent the payment (not whose invoices it pays)">
          Payer
        </T>
      </RemittanceFieldLabel>
      {isEditing ? (
        <div className="flex items-center gap-1">
          {/* A name-sized box, not the whole column: it replaces one line
              of text, so it shouldn't read as a full-width form field. */}
          <div className="w-60 max-w-full min-w-0">
            <CustomersAutocomplete
              selectedCustomerId={payer?.id}
              onCustomerSelect={handleSelect}
              plainTrigger
            />
          </div>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs"
            disabled={override.isPending}
            onClick={() => setIsEditing(false)}
          >
            <T context="cancel changing the payer on a remittance">Cancel</T>
          </Button>
        </div>
      ) : payer ? (
        <div className="flex min-w-0 items-start gap-1">
          <CustomerLink
            customerId={payer.id}
            customerName={payer.name}
            erpDisplayName={payer.erpDisplayName}
            className="min-w-0 font-medium break-words"
          />
          {/* size-5 with no margin keeps the row as tall as the invoiced
              customers' text beside it, so the two columns stay level. */}
          <Button
            size="icon"
            variant="ghost"
            className="size-5 shrink-0 text-muted-foreground"
            disabled={override.isPending}
            onClick={() => setIsEditing(true)}
            aria-label={gt('Change payer', {
              context: 'a11y label, change the payer customer on a remittance',
            })}
          >
            <PencilSimple className="size-3.5" />
          </Button>
        </div>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          disabled={override.isPending}
          onClick={() => setIsEditing(true)}
        >
          <T context="button, assign a customer to a remittance with no match">
            Assign Customer
          </T>
        </Button>
      )}
    </div>
  );
}

/**
 * Whose invoices the remittance pays — often not the payer (a parent paying
 * for its branches, a sister company paying another's bill).
 */
function InvoicedCustomersField({ remittance }: RemittanceDetailsProps) {
  return (
    <div className="min-w-0 space-y-1">
      <RemittanceFieldLabel>
        <T context="label, customers whose invoices a remittance pays">
          Invoiced customers
        </T>
      </RemittanceFieldLabel>
      <InvoicedCustomers
        variant="stack"
        customers={remittance.invoicedCustomers}
        fallback={
          <div className="text-sm text-muted-foreground">
            <T context="empty state, remittance has no line matched to an invoice yet">
              No invoices matched yet
            </T>
          </div>
        }
      />
    </div>
  );
}

/** Total, bank status and Edit — the card's summary row. */
function RemittanceCardHeader({
  remittance,
  onEdit,
}: RemittanceDetailsProps & { onEdit: () => void }) {
  return (
    <CardHeader className="pb-2">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <RemittanceFieldLabel>
            <T>Total Amount</T>
          </RemittanceFieldLabel>
          <div className="text-2xl font-bold text-green-600 tabular-nums">
            <Currency value={remittance.statedTotalAmount} fallback="--" />
          </div>
        </div>
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onEdit}>
            <PencilSimple className="mr-1 size-4" />
            <T context="edit remittance">Edit</T>
          </Button>
        </div>
      </div>
    </CardHeader>
  );
}

/** Payer and invoiced customers first, then the stated details, two columns. */
function RemittanceFields({ remittance }: RemittanceDetailsProps) {
  const gt = useGT();
  return (
    <CardContent>
      <div className="grid grid-cols-2 gap-x-6 text-sm">
        <PayerField remittance={remittance} />
        <InvoicedCustomersField remittance={remittance} />
      </div>
      {/* Who paid / whose invoices, set apart from the remittance's details. */}
      <div className="my-4 border-t" />
      <div className="grid grid-cols-2 gap-x-6 gap-y-5 text-sm">
        <div className="space-y-1">
          <RemittanceFieldLabel>
            <T>Received Date</T>
          </RemittanceFieldLabel>
          <div className="font-medium">
            {remittance.receivedAt
              ? new Date(remittance.receivedAt).toLocaleDateString()
              : '--'}
          </div>
        </div>

        {remittance.statedPaymentDate && (
          <div className="space-y-1">
            <RemittanceFieldLabel>
              <T>Payment Date</T>
            </RemittanceFieldLabel>
            <div className="font-medium">
              {formatDateOnlyString(remittance.statedPaymentDate)}
            </div>
          </div>
        )}

        {remittance.statedPaymentMethod && (
          <div className="space-y-1">
            <RemittanceFieldLabel>
              <T>Payment Method</T>
            </RemittanceFieldLabel>
            <div className="font-medium">
              {remittance.statedPaymentMethod
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (c: string) => c.toUpperCase())}
            </div>
          </div>
        )}

        {remittance.statedReferenceNo && (
          <div className="space-y-1">
            <RemittanceFieldLabel>
              <T>Reference</T>
            </RemittanceFieldLabel>
            <div className="font-medium">{remittance.statedReferenceNo}</div>
          </div>
        )}

        {remittance.linkedReceipt && (
          <div className="space-y-1">
            <RemittanceFieldLabel>
              <T context="remittance detail label, the cash receipt the remittance is linked to">
                Linked Receipt
              </T>
            </RemittanceFieldLabel>
            <div>
              <Link
                to="/accounts-receivable/cash-receipts"
                search={{ receiptId: remittance.linkedReceipt.id }}
                viewTransition
                className="font-medium text-blue-600 hover:underline"
              >
                {remittance.linkedReceipt.referenceNumber ??
                  gt('View receipt', {
                    context: 'linked cash receipt link fallback on remittance',
                  })}
              </Link>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Currency
                  value={remittance.linkedReceipt.amount}
                  fallback="--"
                />
                {remittance.linkedReceipt.transactionDate && (
                  <span>
                    ·{' '}
                    {formatDateOnlyString(
                      remittance.linkedReceipt.transactionDate,
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {remittance.senderEmail && (
          <div className="min-w-0 space-y-1">
            <RemittanceFieldLabel>
              <T>Sender</T>
            </RemittanceFieldLabel>
            <div
              className="truncate font-medium"
              title={remittance.senderEmail}
            >
              {remittance.senderEmail}
            </div>
          </div>
        )}
      </div>
    </CardContent>
  );
}

export function RemittanceMetadataPanel({
  remittance,
}: RemittanceDetailsProps) {
  const gt = useGT();
  const { showToast } = useToast();
  // Remittances are editable on every channel: ingested ones (email/EDI/portal)
  // are never re-synced, so corrections to extraction errors stick.
  const [editOpen, setEditOpen] = useState(false);
  const deleteLineItem = useDeleteRemittanceLineItem();

  async function handleDeleteLineItem(lineItemId: string) {
    try {
      await deleteLineItem.mutateAsync({ lineItemId });
      showToast({
        title: gt('Line item removed', {
          context: 'toast title, remittance line item removed',
        }),
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      showToast({
        title: gt('Remove failed', {
          context: 'toast title, remittance line item remove failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto space-y-4">
      <Card>
        <RemittanceCardHeader
          remittance={remittance}
          onEdit={() => setEditOpen(true)}
        />

        <RemittanceFields remittance={remittance} />
      </Card>

      <RemittanceVerificationPanel
        verificationResult={remittance.verificationResult}
      />

      <Separator />
      <RemittanceLineItemsTable
        heading={
          <div className="text-sm font-semibold">
            <T context="ar remittance">Line Items</T>
          </div>
        }
        headingAside={
          <div className="text-right text-xs text-muted-foreground">
            <T context="hint, remittance line item negative amount means deduction">
              Tip: enter a negative amount for a deduction.
            </T>
          </div>
        }
        lineItems={remittance.lineItems}
        editable
        remittanceAdviceId={remittance.id}
        onDelete={handleDeleteLineItem}
        deletingId={
          deleteLineItem.isPending ? deleteLineItem.variables?.lineItemId : null
        }
      />

      <RemittanceFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        remittance={remittance}
      />
    </div>
  );
}

export function RemittanceDetails({ remittance }: RemittanceDetailsProps) {
  const { value: panelSize, set: setPanelSize } = useLocalStorageValue<number>(
    'ar-remittance-details-panel-size',
    {
      defaultValue: 50,
      initializeWithValue: true,
    },
  );

  const { sourceEmail, documentId } = remittance;

  if (!sourceEmail && !documentId) {
    return (
      <div className="flex h-full flex-col px-3 py-3">
        <RemittanceMetadataPanel remittance={remittance} />
        <div className="mt-4 flex justify-center">
          <LinkEmailButton remittanceAdviceId={remittance.id} />
        </div>
      </div>
    );
  }

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full gap-2 px-3">
      <ResizablePanel
        defaultSize={panelSize}
        onResize={(size) => setPanelSize(size)}
        minSize={25}
        className="flex flex-col pr-2"
      >
        <RemittanceMetadataPanel remittance={remittance} />
        {/* A document-backed remittance can still have its email linked later. */}
        {!sourceEmail && (
          <div className="mt-4 flex justify-center">
            <LinkEmailButton remittanceAdviceId={remittance.id} />
          </div>
        )}
      </ResizablePanel>
      <ResizableHandle />
      <ResizablePanel minSize={20}>
        {sourceEmail ? (
          <SmartEmailViewer
            emailData={inboundEmailToEmailData(sourceEmail)}
            conversationId={sourceEmail.conversationId}
            defaultTab="email"
            sourceEmailId={sourceEmail.id}
            highlightedEmailIds={[sourceEmail.id]}
          />
        ) : (
          // No email behind it (a portal download, a bank memo): show the
          // document the advice was extracted from.
          documentId && (
            <DocumentViewer documentId={documentId} className="h-full" />
          )
        )}
      </ResizablePanel>
    </ResizablePanelGroup>
  );
}
